import { useLayoutEffect, useRef, useState } from 'react';
import { toPositionTree, countPositions } from './orgChartPositions';
import './orgChartPrint.css';

/**
 * The job-positions sheet: the org's structure with the people taken out, laid
 * out as a real tree and scaled to fit ONE landscape page.
 *
 * Different from OrgPrintView, which prints the full chart as one section per
 * manager across as many pages as it needs. That document answers "who reports
 * to whom"; this one answers "what positions exist", and its whole value is
 * being a single sheet you can put on a wall.
 *
 * Fitting is done by measuring, not by guessing. The tree is rendered at its
 * natural size, measured, and then scaled by the smaller of the width and height
 * ratios so the longer axis is the one that fits. A CSS-only approach cannot do
 * this: the natural size depends on how the browser wrapped the titles, which is
 * only known after layout.
 */

/**
 * A4 landscape content box in CSS px at 96dpi, less the 10mm @page margin.
 *
 * HEADER_PX is subtracted from the height before the tree is fitted. Fitting to
 * the full content height is what produced a two-page PDF in testing: the tree
 * filled the page exactly, the header sat above it, and the sum overflowed onto a
 * second sheet with page one left blank below its own header. The header is a
 * fixed-height band (logo, title, caption, rule) so a constant is honest here;
 * measuring it would only re-derive the same number.
 */
const HEADER_PX = 78;
const PAGE = { w: 1047, h: 718 };

function PositionNode({ node }) {
  const kids = node.children || [];
  return (
    <li className="ocp-node">
      <div className="ocp-card">
        <span className="ocp-card__title">{node.title}</span>
      </div>
      {kids.length > 0 && (
        <ul className="ocp-children">
          {kids.map((c) => <PositionNode key={c.id} node={c} />)}
        </ul>
      )}
    </li>
  );
}

export default function OrgPositionsPrintView({ roots, title, caption, printedOn }) {
  const treeRef = useRef(null);
  // 1 until measured. Rendering at natural size first is what makes the
  // measurement meaningful; scaling is applied on the second pass.
  const [scale, setScale] = useState(1);

  const positions = toPositionTree(roots);
  const total = countPositions(positions);
  const page = PAGE;

  // useLayoutEffect, not useEffect: the scale must be in the DOM before the
  // browser paints the frame the print dialog snapshots. A paint at natural size
  // followed by a scaled one would race window.print().
  useLayoutEffect(() => {
    const el = treeRef.current;
    if (!el) return;
    // Measure unscaled. scrollWidth/Height are layout values and so are unaffected
    // by the transform already applied on a re-run, but the transform origin means
    // the box the parent reserves does change, so the ratio is taken from the
    // element's own untransformed content size.
    const w = el.scrollWidth;
    const h = el.scrollHeight;
    if (!w || !h) return;
    // Never scale UP: a small department should print at its natural size rather
    // than being blown up to fill the sheet.
    const next = Math.min(1, page.w / w, (page.h - HEADER_PX) / h);
    // Round, so a sub-pixel difference between renders cannot loop this effect.
    const rounded = Math.max(0.05, Math.round(next * 1000) / 1000);
    setScale((prev) => (Math.abs(prev - rounded) < 0.002 ? prev : rounded));
  }, [page.w, page.h, total]);

  return (
    <div className="ocp-doc" aria-hidden="true">
      <div className="ocp-head">
        <img className="ocp-logo" src="/images/rtg-logo-red.png" alt="" />
        <div className="ocp-head__text">
          <h1 className="ocp-title">{title}</h1>
          <p className="ocp-caption">{caption}</p>
        </div>
        <div className="ocp-head__meta">
          <span>{total} positions</span>
          <span>{printedOn}</span>
        </div>
      </div>

      {/* The scaled box. transform: scale() paints smaller but reserves the
          unscaled box, so the wrapper's height is pinned to the scaled height or
          the sheet would carry a page of empty space underneath. */}
      <div
        className="ocp-fit"
        style={{ height: treeRef.current ? treeRef.current.scrollHeight * scale : undefined }}
      >
        <div
          className="ocp-fit__inner"
          ref={treeRef}
          style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}
        >
          <ul className="ocp-tree">
            {positions.map((p) => <PositionNode key={p.id} node={p} />)}
          </ul>
        </div>
      </div>

      {positions.length === 0 && (
        <p className="ocp-empty">No positions in this scope.</p>
      )}
    </div>
  );
}
